<template>
  <div class="subject" :class="{ 'subject--is-expanded': computedIsExpanded }">
    <div class="subject__index d-flex align-items-center">
      <span class="subject__sequence">第 {{ index + 1 }} 題</span>
      <i :class="subjectTypeIcon(type)" class="mdi mdi-18px ml-3"></i>
    </div>

    <div class="subject__title">
      <b-form-input
        :class="{ 'is-invalid': validate && titleValidState === false }"
        ref="titleInputRef"
        v-model="vModelForTitle"
        @input="value => $emit('update:title', value)"
        :disabled="isReadOnly || false === titleIsEditable"
        :state="validate ? titleValidState : null"
        :placeholder="validate && titleValidState === false ? validationMessage : ''"
      />
    </div>

    <div class="subject__sorting d-flex align-items-center">
      <i class="mdi mdi-24px mdi-drag-horizontal cursor-grab subject-layout-drag-handler"></i>
      <b-button class="ml-2 subject__button" :class="{ 'subject__button--disabled': moveUpIsDisabled || isReadOnly }" variant="secondary" @click="$emit('top')" :disabled="isReadOnly">
        <span class="mdi mdi-arrow-collapse-up"></span>
        移至最頂
      </b-button>
      <b-button class="ml-2 subject__button" :class="{ 'subject__button--disabled': moveUpIsDisabled || isReadOnly }" variant="secondary" @click="$emit('up')" :disabled="isReadOnly">
        <span class="mdi mdi-arrow-up"></span>
        上移
      </b-button>
      <b-button class="ml-2 subject__button" :class="{ 'subject__button--disabled': moveDownIsDisabled || isReadOnly }" variant="secondary" @click="$emit('down')" :disabled="isReadOnly">
        <span class="mdi mdi-arrow-down"></span>
        下移
      </b-button>
      <b-button class="ml-2 subject__button" :class="{ 'subject__button--disabled': moveDownIsDisabled || isReadOnly }" variant="secondary" @click="$emit('bottom')" :disabled="isReadOnly">
        <span class="mdi mdi-arrow-collapse-down"></span>
        移至最底
      </b-button>
    </div>

    <div class="subject__deletion d-flex align-items-center">
      <b-button class="subject__button" variant="danger" @click="confirmForDeletion" :disabled="isReadOnly" v-if="isDeletable">
        <span class="mdi mdi-delete"></span>
        移除
      </b-button>
      <b-button class="subject__button" variant="primary" :disabled="true" v-else>
        <span class="mdi mdi-star"></span>
        必要
      </b-button>
    </div>

    <div class="subject__options" :class="{ 'd-none': !computedIsExpanded }">
      <b-input-group class="subject__advance-option-input" v-if="placeholderIsVisible">
        <b-input-group-prepend>
          <span class="mdi mdi-information-outline"></span>
        </b-input-group-prepend>
        <b-form-input ref="placeholderRef" v-model="vModelForPlaceholder" @input="value => $emit('update:placeholder', value)"></b-form-input>
      </b-input-group>
      <b-input-group class="subject__advance-option-input" v-if="memoIsVisible">
        <b-input-group-prepend>
          <span class="mdi mdi-comment-text-outline"></span>
        </b-input-group-prepend>
        <b-form-input ref="memoRef" v-model="vModelForMemo" @input="value => $emit('update:memo', value)"></b-form-input>
      </b-input-group>
      <b-input-group class="subject__advance-option-input" v-if="locationIsVisible">
        <b-input-group-prepend>
          <span class="mdi mdi-image-filter-center-focus-weak"></span>
        </b-input-group-prepend>
        <div class="subject__advance-option-content-wrapper">
          <span>分頁：</span>
          <b-form-select
            v-model="vModelForPage"
            :options="pageCandidates"
            @input="handlePageChanges"
            style="max-width: 120px"
          ></b-form-select>
          <span>，分區：</span>
          <b-form-select
            v-model="vModelForSection"
            :options="sectioncandidates"
            @input="handleSectionChanges"
            style="max-width: 120px"
          ></b-form-select>
        </div>
      </b-input-group>
      <template v-if="visibleConditionIsVisible">
        <b-input-group class="subject__advance-option-input" v-for="(conditionIndex) in displayConditions" :key="conditionIndex">
            <b-input-group-prepend>
              <span class="mdi mdi-lightbulb-on-outline"></span>
            </b-input-group-prepend>
            <div class="subject__advance-option-content-wrapper">
              <span>當</span>
              <b-form-select
                v-model="conditionList[conditionIndex - 1].field_id"
                :options="visibleConditionFieldCandidates"
                @input="val => handleVisibleConditionFieldChanges(conditionIndex - 1, val)"
                style="max-width: 160px"
              ></b-form-select>
              <span>為</span>
              <b-form-select
                v-model="conditionList[conditionIndex - 1].value"
                :options="visibleConditionValueCandidates"
                @input="val => handleVisibleConditionOptionChanges(conditionIndex - 1, val)"
                style="max-width: 160px"
              ></b-form-select>
              <!-- 增加減少 -->
              <b-button class="ml-2 p-1 d-inline-flex align-items-center" variant="inverse-success" @click="createCondition(conditionIndex - 1)">
                <span class="mdi mdi-18px mdi-playlist-plus"></span>
                增加
              </b-button>
              <b-button class="ml-2 p-1 d-inline-flex align-items-center" variant="inverse-danger" @click="removeCondition(conditionIndex - 1)" :disabled="isOnlyOneCondition()">
                <span class="mdi mdi-18px mdi-playlist-remove"></span>
                移除
              </b-button>
            </div>

        </b-input-group>
      </template>
      <span class="subject__validation-rule mb-3" v-if="vModelForValidationRules">
        格式：{{ deepGet(allValidationRules, `[${type}][${vModelForValidationRules}].title`, '') }}
      </span>
      <div class="subject__toggle-wrapper">
        <b-button class="subject__button" :variant="vModelForRequired ? 'primary' : 'secondary'" @click="vModelForRequired = !vModelForRequired; $emit('update:required', vModelForRequired)" :disabled="isReadOnly">
          <span class="mdi mdi-star-outline"></span>
          必填
        </b-button>
        <b-button class="subject__button" :variant="vModelForValidationRules ? 'primary' : 'secondary'" :disabled="isReadOnly" v-if="Object.keys(allValidationRules).includes(type)" @click="validationModalIsVisible = true">
          <span class="mdi mdi-altimeter"></span>
          格式驗證
        </b-button>
        <b-button class="subject__button" :variant="placeholderIsVisible ? 'primary' : 'secondary'" @click="togglePlaceholderValue" v-if="hasPlaceholder" :disabled="isReadOnly">
          <span class="mdi mdi-information-outline"></span>
          提示文字
        </b-button>
        <b-button class="subject__button" :variant="memoIsVisible ? 'primary' : 'secondary'" @click="toggleMemoValue" :disabled="isReadOnly">
          <span class="mdi mdi-comment-text-outline"></span>
          填寫說明
        </b-button>
        <b-button class="subject__button" :variant="vModelForAssignToCustomerData ? 'primary' : 'secondary'" :disabled="isReadOnly" v-if="type === 'text'" @click="dataAssignmentModalIsVisible = true">
          <span class="mdi mdi-link-variant"></span>
          欄位自動預填
        </b-button>
        <b-button class="subject__button" :variant="vModelForSaveToCustomerData ? 'primary' : 'secondary'" :disabled="isReadOnly" v-if="type === 'text'" @click="saveToCustomerDataModalIsVisible = true">
          <span class="mdi mdi-content-save-outline"></span>
          自動更新客資
        </b-button>
        <b-button class="subject__button" :variant="locationIsVisible ? 'primary' : 'secondary'" @click="toggleLocation" :disabled="isReadOnly" v-if="locationIsEnabled">
          <span class="mdi mdi-image-filter-center-focus-weak"></span>
          顯示區域
        </b-button>
        <b-button class="subject__button" :variant="visibleConditionIsVisible ? 'primary' : 'secondary'" @click="toggleVisibleCondition" :disabled="isReadOnly" v-if="visibleConditionIsEnabled">
          <span class="mdi mdi-lightbulb-on-outline"></span>
          觸發顯示
        </b-button>
        <b-button v-if="formType === 'booking'" class="subject__button" :variant="vModelForDashboardOnly ? 'primary' : 'secondary'" @click="toggleDashboardOnly" :disabled="isReadOnly">
          <span class="mdi mdi-laptop"></span>
          僅限後台編輯
        </b-button>
        <b-button v-if="formType === 'collection'" class="subject__button" :variant="vModelForMainDisplay ? 'primary' : 'secondary'" @click="toggleMainDisplay" :disabled="isReadOnly">
          <span class="mdi mdi-label-outline"></span>
          下拉選單以這欄位當作顯示文字
        </b-button>
        <b-button v-if="['booking', 'preorder'].includes(formType)" class="subject__button" :variant="vModelForListHidden ? 'primary' : 'secondary'" @click="toggleListHidden" :disabled="isReadOnly">
          <span class="mdi mdi-eye-off"></span>
          隱藏於列表欄位
        </b-button>
        <span v-if="formType === 'collection'" :id="`not-repeatable-tooltip-${index}`" class="d-inline-flex">
          <b-button class="subject__button" :variant="vModelForNotRepeatable ? 'primary' : 'secondary'" @click="toggleNotRepeatable" :disabled="isReadOnly">
            <span class="mdi mdi-content-duplicate-off"></span>
            設定為不可重複
          </b-button>
        </span>
        <b-tooltip v-if="formType === 'collection'" :target="`not-repeatable-tooltip-${index}`" triggers="hover focus">
          若有多個欄位皆設為不可重複，代表單一欄位可重複，但加起來不可重複
        </b-tooltip>
      </div>
    </div>

    <div class="subject__preview" :class="{ 'd-none': !computedIsExpanded }">
      <span class="font-weight-bold">預覽：</span>
      <b-card class="rounded rounded-lg subject__preview-box">
        <slot name="preview"></slot>
      </b-card>
    </div>

    <div class="subject__slot" :class="{ 'd-none': !computedIsExpanded }">
      <slot></slot>
    </div>

    <div class="subject__expand" v-if="expandButtonIsVisible">
      <b-button class="d-flex align-items-center justify-content-center w-100 border-0 bg-white" @click="toggleDetails">
        <i class="mdi mdi-24px mdi-chevron-down"></i>
      </b-button>
    </div>

    <b-modal
      v-model="validationModalIsVisible"
      title="格式驗證"
      centered
    >
      <template>
        <b-form-group label="選擇驗證規則">
          <b-form-select
            v-model="vModelForValidationRules"
            :options="validationRuleOptions"
            @input="$emit('update:validationRules', vModelForValidationRules)"
          ></b-form-select>
        </b-form-group>
      </template>
      <template #modal-footer>
        <div class="text-right">
          <b-button
            variant="primary"
            size="sm"
            @click="validationModalIsVisible = false"
          >
            確定
          </b-button>
        </div>
      </template>
    </b-modal>

    <b-modal
      v-model="dataAssignmentModalIsVisible"
      title="欄位自動預填"
      centered
    >
      <template>
        <b-form-group label="請選擇自動預填的欄位">
          <b-form-select
            v-model="vModelForAssignToCustomerData"
            :options="assignableCustomerDataOptions"
            @input="handleAssignToCustomerDataChange"
          ></b-form-select>
          <span class="text-muted small mt-2 d-block">
            ※ 設定後，若系統已有此客戶的資料，將在預約時自動預填。
          </span>
        </b-form-group>
        <b-form-group label="請輸入自訂欄位名稱" v-if="vModelForAssignToCustomerData === 'custom'">
          <b-form-input
            v-model="vModelForCustomFieldName"
            @input="$emit('update:assignToCustomerData', vModelForCustomFieldName)"
            placeholder="例如: mobile_phone 或 extra.fav_store_name"
          ></b-form-input>
          <span class="text-muted small mt-2 d-block">
            ※ 支援層級路徑，例如: extra.fav_store_name
          </span>
        </b-form-group>
      </template>
      <template #modal-footer>
        <div class="text-right">
          <b-button
            variant="primary"
            size="sm"
            @click="dataAssignmentModalIsVisible = false"
          >
            確定
          </b-button>
        </div>
      </template>
    </b-modal>

    <b-modal
      v-model="saveToCustomerDataModalIsVisible"
      title="自動更新客資"
      centered
    >
      <template>
        <b-form-group label="請選擇要儲存至客戶資料的欄位">
          <b-form-select
            v-model="vModelForSaveToCustomerData"
            :options="savableCustomerDataOptions"
            @input="handleSaveToCustomerDataChange"
          ></b-form-select>
          <span class="text-muted small mt-2 d-block">
            ※ 設定後，客戶填寫的內容將自動儲存至指定的客戶資料欄位。
          </span>
        </b-form-group>
        <b-form-group label="請輸入自訂欄位名稱" v-if="vModelForSaveToCustomerData === 'custom'">
          <b-form-input
            v-model="vModelForSaveCustomFieldName"
            @input="$emit('update:saveToCustomerData', vModelForSaveCustomFieldName)"
            placeholder="例如: mobile_phone 或 extra.fav_store_name"
          ></b-form-input>
          <span class="text-muted small mt-2 d-block">
            ※ 支援層級路徑，例如: extra.fav_store_name
          </span>
        </b-form-group>
      </template>
      <template #modal-footer>
        <div class="text-right">
          <b-button
            variant="primary"
            size="sm"
            @click="saveToCustomerDataModalIsVisible = false"
          >
            確定
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import DynamicFormField from '@/models/DynamicFormField.model'
import { subjectTypeIcon, validationRules as validationRulesDefinition } from '@/utils/DynamicFormUtils';
import _ from "lodash";

export default {
  props: {
    index: {
      type: Number,
      required: true,
    },
    type: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      default: '',
    },
    memo: {
      type: String,
      default: '',
    },
    assignToCustomerData: {
      type: String,
      default: '',
    },
    saveToCustomerData: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    validationRules: {
      type: String,
      default: null,
    },
    dashboardOnly: {
      type: Boolean,
      default: false,
    },
    mainDisplay: {
      type: Boolean,
      default: false,
    },
    listHidden: {
      type: Boolean,
      default: false,
    },
    notRepeatable: {
      type: Boolean,
      default: false,
    },
    moveUpIsDisabled: {
      type: Boolean,
      default: false,
    },
    moveDownIsDisabled: {
      type: Boolean,
      default: false,
    },
    hasPlaceholder: {
      type: Boolean,
      default: true,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
    formType: {
      type: String,
      default: 'survey',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    page: {
      type: String,
      required: false,
    },
    pageOptions: {
      type: Array,
      default: () => ([]),
    },
    section: {
      type: String,
      required: false,
    },
    sectionOptions: {
      type: Array,
      default: () => ([]),
    },
    visibleConditions: {
      type: Array,
      default: () => ([]),
    },
    fieldOptionsForVisibleConditions: {
      type: Array,
      default: () => ([]),
    },
    validate: {
      type: Boolean,
      default: false,
    },
    validationMessage: {
      type: String,
      default: '此欄位為必填',
    },
    titleIsEditable: {
      type: Boolean,
      default: true,
    },
    isDeletable: {
      type: Boolean,
      default: true,
    },
    expandButtonIsVisible: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      detailIsExpanded: false,
      vModelForTitle: '',
      vModelForRequired: false,
      vModelForValidationRules: null,
      validationModalIsVisible: false,
      dataAssignmentModalIsVisible: false,
      vModelForAssignToCustomerData: null,
      vModelForMemo: '',
      vModelForPlaceholder: '',
      memoIsVisible: false,
      placeholderIsVisible: false,
      vModelForDashboardOnly: false,
      vModelForMainDisplay: false,
      vModelForListHidden: false,
      vModelForNotRepeatable: false,
      titleInputRef: null,
      vModelForPage: null,
      vModelForSection: null,
      locationIsVisible: false,
      visibleConditionIsVisible: false,
      displayConditions: 1,
      conditionList: [],
      vModelForCustomFieldName: '',
      saveToCustomerDataModalIsVisible: false,
      vModelForSaveToCustomerData: null,
      vModelForSaveCustomFieldName: '',
    }
  },
  computed: {
    isReadOnly() {
      return this.readonly
    },
    computedIsExpanded() {
      return this.expanded || this.detailIsExpanded
    },
    locationIsEnabled() {
      return ['BookingPresetEdit'].includes(this.$route.name);
    },
    visibleConditionIsEnabled() {
      return ['BookingPresetEdit'].includes(this.$route.name);
    },
    pageCandidates() {
      return [
        { text: '不設定分頁', value: null },
        ...this.pageOptions.map(option => ({ text: option.title, value: option._id })),
      ]
    },
    sectioncandidates() {
      return [
        { text: '不設定分區', value: null },
        ...this.sectionOptions
          .filter(option => this.vModelForPage ? option.page_id === this.vModelForPage : !option.page_id)
          .map(option => ({ text: option.title, value: option._id })),
      ]
    },
    visibleConditionFieldCandidates() {
      return [
        { text: '特定題目', value: null },
        ...this.fieldOptionsForVisibleConditions.filter(field => Object.keys(field).includes('options')).map(field => ({ text: field.title, value: field._id })),
      ]
    },
    visibleConditionValueCandidates() {
      return [
        { text: '特定選項', value: null },
        ..._.get(this.fieldOptionsForVisibleConditions.find(field => this.conditionList.map(cond => cond.field_id).includes(field._id)), 'options', []),
      ]
    },
    titleValidState(){
      const passStyle = null;
      const errorStyle = false;
      return this.vModelForTitle.trim() ?  passStyle : errorStyle;
    },
    allValidationRules() {
      return validationRulesDefinition
    },
    validationRuleOptions() {
      return [
        { text: '不設定', value: null },
        ...Object.keys(_.get(this.allValidationRules, this.type, []))
          .map(rule => ({ text: this.allValidationRules[this.type][rule].title, value: rule })),
      ]
    },
    assignableCustomerDataOptions() {
      return [
        { text: '不設定', value: null },
        ...DynamicFormField.assignableCustomerDataFields().map(field => ({ text: field.displayName, value: field.key })),
      ]
    },
    savableCustomerDataOptions() {
      return [
        { text: '不設定', value: null },
        ...DynamicFormField.assignableCustomerDataFields().map(field => ({ text: field.displayName, value: field.key })),
      ]
    },
  },
  mounted() {
    this.vModelForTitle = this.title
    this.vModelForRequired = this.required
    this.vModelForValidationRules = this.validationRules
    this.vModelForMemo = this.memo
    this.vModelForAssignToCustomerData = this.assignToCustomerData || null
    this.vModelForSaveToCustomerData = this.saveToCustomerData || null
    this.vModelForPlaceholder = this.placeholder
    this.vModelForDashboardOnly = this.dashboardOnly
    this.vModelForMainDisplay = this.mainDisplay
    this.vModelForListHidden = this.listHidden
    this.vModelForNotRepeatable = this.notRepeatable

    this.memoIsVisible = !!this.vModelForMemo
    this.placeholderIsVisible = !!this.vModelForPlaceholder
    this.detailIsExpanded = this.expanded

    if (!!this.page || !!this.section) {
      this.locationIsVisible = true
      this.vModelForPage = this.page || null
      this.vModelForSection = this.section || null
    }

    if (this.visibleConditions.length > 0) {
      this.visibleConditionIsVisible = true
      this.conditionList = this.visibleConditions.map(condition => ({
        field_id: condition.field_id,
        value: condition.value,
      }))
      this.displayConditions = this.conditionList.length
    }

    // 初始化自訂欄位 (自動預填)
    const predefinedKeys = DynamicFormField.assignableCustomerDataFields()
      .map(field => field.key)
      .filter(key => key !== 'custom');
    
    if (this.vModelForAssignToCustomerData && !predefinedKeys.includes(this.vModelForAssignToCustomerData)) {
      this.vModelForCustomFieldName = this.vModelForAssignToCustomerData;
      this.vModelForAssignToCustomerData = 'custom';
    }

    // 初始化自訂欄位 (自動更新客資)
    if (this.vModelForSaveToCustomerData && !predefinedKeys.includes(this.vModelForSaveToCustomerData)) {
      this.vModelForSaveCustomFieldName = this.vModelForSaveToCustomerData;
      this.vModelForSaveToCustomerData = 'custom';
    }
  },
  methods: {
    deepGet: _.get,
    subjectTypeIcon,
    createCondition(index) {
      this.conditionList.splice(index + 1, 0, {
        field_id: null,
        value: null,
      });
      this.displayConditions = this.conditionList.length;
    },
    removeCondition(index) {
      this.conditionList.splice(index, 1);
      this.displayConditions = this.conditionList.length;
      this.$emit('update:visibleConditions', _.cloneDeep(this.conditionList))
    },
    isOnlyOneCondition() {
      return this.displayConditions === 1;
    },
    async confirmForDeletion() {
      const consented = await this.$swal({
        title: this.title ? `確認刪除【${this.title}】嗎？` : '確認刪除嗎？',
        // text: ,
        type: "warning",
        showConfirmButton: true,
        showCancelButton: true,
        cancelButtonColor: "#3085d6",
        cancelButtonText: "不刪除",
        confirmButtonColor: "#d33",
        confirmButtonText: "確定刪除",
        reverseButtons: false,
      })

      if (!consented.value) {
        return
      }

      this.$emit('remove')
    },
    toggleMemoValue() {
      if (this.memoIsVisible) {
        this.vModelForMemo = ''
        this.$emit('update:memo', '')
      }

      this.memoIsVisible = !this.memoIsVisible

      this.$nextTick(() => {
        if (this.memoIsVisible) {
          this.$refs.memoRef.focus()
        }
      })
    },
    togglePlaceholderValue() {
      if (this.placeholderIsVisible) {
        this.vModelForPlaceholder = ''
        this.$emit('update:placeholder', '')
      }

      this.placeholderIsVisible = !this.placeholderIsVisible

      this.$nextTick(() => {
        if (this.placeholderIsVisible) {
          this.$refs.placeholderRef.focus()
        }
      })
    },
    toggleLocation() {
      this.locationIsVisible = !this.locationIsVisible

      if (this.locationIsVisible === false) {
        this.vModelForPage = null
        this.vModelForSection = null
        this.$emit('update:page', this.vModelForPage)
        this.$emit('update:section', this.vModelForSection)
      }
    },
    toggleVisibleCondition() {
      this.visibleConditionIsVisible = !this.visibleConditionIsVisible

      if (!this.visibleConditionIsVisible) {
        this.conditionList = []
        this.$emit('update:visibleConditions', [])
        this.displayConditions = 1
      } else {
        this.conditionList = [{
          field_id: null,
          value: null,
        }]
      }
    },
    toggleDetails() {
      this.detailIsExpanded = !this.computedIsExpanded
      this.$emit('update:expanded', this.detailIsExpanded)
    },
    toggleDashboardOnly() {
      this.vModelForDashboardOnly = !this.vModelForDashboardOnly;
      this.$emit('update:dashboardOnly', this.vModelForDashboardOnly);
    },
    toggleMainDisplay() {
      this.vModelForMainDisplay = !this.vModelForMainDisplay;
      this.$emit('update:mainDisplay', this.vModelForMainDisplay);
    },
    toggleListHidden() {
      this.vModelForListHidden = !this.vModelForListHidden;
      this.$emit('update:listHidden', this.vModelForListHidden);
    },
    toggleNotRepeatable() {
      this.vModelForNotRepeatable = !this.vModelForNotRepeatable;
      this.$emit('update:notRepeatable', this.vModelForNotRepeatable);
    },
    handlePageChanges() {
      this.$emit('update:page', this.vModelForPage)
      this.vModelForSection = null
      this.$emit('update:section', this.vModelForSection)
    },
    handleSectionChanges() {
      this.$emit('update:section', this.vModelForSection)
    },
    handleVisibleConditionFieldChanges(conditionIndex, val) {
      const originalCondition = _.cloneDeep(this.conditionList);
      if(val){
        this.conditionList[conditionIndex] = {
          field_id: val,
          value: null,
        };
        this.$emit('update:visibleConditions', _.cloneDeep(this.conditionList))
      } else {
        originalCondition.splice(conditionIndex, 1);
        this.$emit('update:visibleConditions', originalCondition)
      }
    },
    handleVisibleConditionOptionChanges(conditionIndex, val) {
      this.conditionList[conditionIndex].value = val;
      this.$emit('update:visibleConditions', _.cloneDeep(this.conditionList))
    },
    handleAssignToCustomerDataChange(value) {
      if (value === 'custom') {
        this.vModelForCustomFieldName = '';
      } else {
        this.vModelForCustomFieldName = '';
        this.$emit('update:assignToCustomerData', value);
      }
    },
    handleSaveToCustomerDataChange(value) {
      if (value === 'custom') {
        this.vModelForSaveCustomFieldName = '';
      } else {
        this.vModelForSaveCustomFieldName = '';
        this.$emit('update:saveToCustomerData', value);
      }
    },
  }
}
</script>

<style lang="scss" scoped>
@use "@/assets/scss/base-colors";

.subject {
  display: grid;
  grid-template-columns: 114px auto 350px 60px;
  grid-gap: 0 16px;
  grid-template-areas:
    "index  title   sorting deletion"
    ".      options preview preview"
    "slot   slot    preview preview"
    "expand expand  expand  expand";
  scroll-margin-top: 80px;

  $sections: index, title, sorting, deletion, options, preview, slot, expand;
  @each $sectionName in $sections {
    &__#{$sectionName} {
      grid-area: $sectionName;
    }
  }

  &--is-expanded {
    .subject__expand i {
      transform: rotate(180deg);
    }
  }
  .is-invalid::placeholder {
    color: #dc3545;
  }
  .subject__sequence {
    border-radius: 4px;
    padding: 4px 12px;
    background-color: #eeeeee;
    font-size: .875rem;
  }
  &__options {
    margin-top: 16px;
  }
  &__slot {
    margin-top: 16px;
  }
  &__preview {
    margin-top: 16px;
  }
  &__advance-option-input {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    margin-bottom: 8px;
    border-radius: 8px;
    background-color: #eee;

    .input-group-prepend {
      background-color: transparent;
    }

    span.mdi {
      margin-left: 12px;
    }

    input.form-control {
      border: none;
      background-color: transparent;
    }

    select.custom-select {
      margin: 0 12px;
      font-size: 0.75rem;
      font-weight: 300;
    }
  }

  &__advance-option-content-wrapper {
    display: flex;
    align-items: center;
    flex: 1 1 auto;
    padding: 8px 12px;

    span {
      font-size: 0.75rem;
      font-weight: 300;
      white-space: nowrap;
    }
  }

  &__button {
    padding: 4px 6px;

    &--disabled {
      opacity: 0.5;
      cursor: not-allowed !important;
    }
  }

  &__validation-rule {
    display: inline-block;
    border: 1px solid map-get(base-colors.$theme-colors, warning);
    border-radius: 8px;
    padding: 4px 8px;
    color: map-get(base-colors.$theme-colors, warning);
    font-size: 0.75rem;
  }

  &__toggle-wrapper {
    margin: -2px -4px;

    > * {
      margin: 2px 4px;
    }
  }

  .subject__preview-box {
    background-color: #e7e7e7;
  }
}
</style>
